import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'

import CircularLoading from '../../../../../../../components/CircularProgress'
import { message, Table, TableColumnsType } from 'antd'
import { Tag } from '../../../../../../../components/Tag'
import PageUrls from '../../../../../../../constants/page-urls'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Link, useNavigate } from 'react-router-dom'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import {
  ETrackingNotificationsStatusFollow,
  ITrackingNotifications,
} from '../../../../../../../types/trackingNotification'
import dayjs from 'dayjs'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../../../constants/global'
import { ILead } from '../../../../../../../types/lead'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../../../types/pagination'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'
import Pagination from '../../../../../../../components/Pagination'

interface ILeadImportCustomerRegisterFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  status: string
  date: { startDate: string; endDate: string }
  page?: number
}

export const CalendarTableModal = (
  props: ILeadImportCustomerRegisterFormModalProps,
) => {
  const navigate = useNavigate()
  const { isOpen, onClose, status, date, page = 1 } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [data, setData] = useState<{ startDate: string; endDate: string }>()
  const [filters, setFilters] = useState<any>({
    page: 1,
    perPage: 50,
  })

  useEffect(() => {
    if (page) {
      setFilters({ ...filters, page })
    }
  }, [props])

  useEffect(() => {
    setData(date)
  }, [isOpen, date, status])

  const trackingNotificationList = useAxios<
    PaginationResponseInterface<ITrackingNotifications>
  >({
    method: 'GET',
    url: '/lead/tracking-notification',
    params: {
      page: filters.page,
      perPage: filters.perPage,
      ...data,
      followStatus:
        status === 'all'
          ? undefined
          : status === 'success'
            ? ETrackingNotificationsStatusFollow.DONE
            : ETrackingNotificationsStatusFollow.WAIT,
    },
  })

  const onUpdateTrackingNotificationListSuccess = useCallback(async () => {
    try {
      await trackingNotificationList.mutate()
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการอัพเดทข้อมูล')
    }
  }, [trackingNotificationList])

  const columns: TableColumnsType<ITrackingNotifications & { key: number }> = [
    {
      title: 'ลำดับ',
      dataIndex: 'key',
      key: 'key',
      width: 80,
      align: 'center' as const,
      render: (key: number) => {
        return (
          <Box>
            <Typography variant="subtitle2">
              {key + 1 + (filters.page - 1) * filters.perPage}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'วันที่แจ้งเตือน',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      width: 160,
      align: 'center' as const,
      render: (expiredAt: string) => {
        return (
          <Box>
            <Tag
              color={dayjs().isAfter(dayjs(expiredAt)) ? 'error' : 'success'}
            >
              <Typography variant="subtitle2">
                {dayjs().isAfter(dayjs(expiredAt)) ? 'หมดอายุ' : 'ใช้งานได้'}
              </Typography>
            </Tag>
            <Typography variant="subtitle2" color="text.secondary">
              {dayjs(expiredAt).format(ONLY_DATE_FORMAT)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(expiredAt).format(ONLY_TIME_FORMAT)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'รายการ LEAD',
      dataIndex: 'lead',
      key: 'lead',
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box sx={{ minWidth: 200, textAlign: 'left' }}>
            <Typography variant="subtitle1" color="text.secondary">
              {lead?.name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {lead?.customer.name} {lead?.customer.phone}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              ผู้ดูแล: {lead?.assignTo?.name}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ผู้ติดตาม',
      dataIndex: 'assignTo',
      key: 'assignTo',
      width: 140,
      align: 'center' as const,
      render: (assignTo: any) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {assignTo?.username}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'PIPLINE',
      dataIndex: 'lead',
      key: 'lead',
      width: 140,
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {lead.pipelineName}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'lead',
      key: 'lead',
      width: 120,
      align: 'center' as const,
      render: (lead: ILead) => {
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Tag color={'info'}>
              <Typography variant="subtitle2">
                {lead.activePipelineItemName}
              </Typography>
            </Tag>
          </Box>
        )
      },
    },
    {
      title: 'หมายเหตุ',
      dataIndex: 'note',
      key: 'note',
      align: 'center' as const,
      render: (note: string, trackingNotification: ITrackingNotifications) => {
        const isFinished =
          dayjs(trackingNotification.lead.expiredAt).toDate() <
            dayjs().toDate() || trackingNotification.lead.isFinished
        return (
          <Box sx={{ minWidth: 130, textAlign: 'center' }}>
            {note ? (
              <Typography variant="subtitle2">{note}</Typography>
            ) : (
              <em></em>
            )}

            {isFinished ? (
              <div>
                {' '}
                <Tag color={'error'}>Lead หมดอายุ หรือ สิ้นสุดแล้ว</Tag>
              </div>
            ) : (
              <></>
            )}
          </Box>
        )
      },
    },
    {
      title: 'สถานะการติดตาม',
      dataIndex: 'statusFollow',
      key: 'statusFollow',
      width: 150,
      align: 'center' as const,
      render: (
        statusFollow: ETrackingNotificationsStatusFollow,
        trackingNotification: ITrackingNotifications,
      ) => {
        const isFinished =
          dayjs(trackingNotification.lead.expiredAt).toDate() <
            dayjs().toDate() || trackingNotification.lead.isFinished
        return (
          <>
            <Button
              disabled={isFinished}
              variant="outlined"
              size="small"
              startIcon={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT ? (
                  <AccessTimeIcon color={'warning'} />
                ) : (
                  <CheckCircleOutlineIcon color={'info'} />
                )
              }
              color={
                statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'warning'
                  : 'info'
              }
              onClick={() =>
                changeStatusFollow(
                  statusFollow === ETrackingNotificationsStatusFollow.DONE
                    ? ETrackingNotificationsStatusFollow.WAIT
                    : ETrackingNotificationsStatusFollow.DONE,
                  trackingNotification?.objectId!,
                )
              }
            >
              <Typography variant="subtitle2">
                {statusFollow === ETrackingNotificationsStatusFollow.WAIT
                  ? 'ยังไม่ติดตาม'
                  : 'ติดตามแล้ว'}
              </Typography>
            </Button>
          </>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: undefined, trackingNotification: ITrackingNotifications) => {
        return (
          <Box>
            <IconButton
              component={Link}
              to={`${PageUrls.CRM.Lead.Detail}/${trackingNotification.lead.objectId}`}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  const changeStatusFollow = async (
    status: ETrackingNotificationsStatusFollow,
    trackingNotificationObjectId: string,
  ) => {
    try {
      await axiosInstance.put(
        `/lead/change-status-tracking-notification-follow/${trackingNotificationObjectId}`,
        {
          status,
        },
      )
      onUpdateTrackingNotificationListSuccess()
      message.success('แก้ไขสถานะสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1024px!important',
        },
      }}
    >
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>
        รายละเอียดวันที่ {dayjs(date?.endDate).format(ONLY_DATE_FORMAT)}
      </DialogTitle>
      <DialogContent>
        <CircularLoading isLoading={isLoading} />
        <Table
          size={'middle'}
          columns={columns}
          scroll={{
            x: columns.reduce(
              (pre, cur: any) => (cur.width ? cur.width + pre : pre),
              0,
            ),
          }}
          dataSource={trackingNotificationList?.data?.records.map(
            (trackingNotificrion: ITrackingNotifications, index: number) => {
              return {
                ...trackingNotificrion,
                key: index,
              }
            },
          )}
          bordered
          pagination={false}
        />
        <Pagination
          total={trackingNotificationList?.data?.count || 0}
          current={filters.page!}
          pageSize={filters.perPage!}
          onChange={(page, perPage) =>
            setFilters({ ...filters, page, perPage })
          }
        />
      </DialogContent>
    </Dialog>
  )
}
